import { useState, useEffect } from 'react';
import { useFolderSearch } from '@keepeek/commons';
import cloneDeep from 'lodash/cloneDeep';
import { updateLoadingState, addTreeOption } from '../tree/utils.js';

/**
 * Folder field hook
 */
var useFolderField = function (_a) {
    var folderIds = _a.folderIds;
    var _b = useFolderFieldTreeView(), treeViewOptions = _b.treeViewOptions, setFolderId = _b.setFolderId;
    var _c = useFolderFieldAutocomplete(), autocompleteOptions = _c.autocompleteOptions, setQuery = _c.setQuery;
    var initialOptions = useFolderFieldInitialOptions({ folderIds: folderIds }).initialOptions;
    return {
        autocompleteOptions: autocompleteOptions,
        setQuery: setQuery,
        treeViewOptions: treeViewOptions,
        setFolderId: setFolderId,
        initialOptions: initialOptions
    };
};
/**
 * Get folders for tree view
 */
var useFolderFieldTreeView = function () {
    var _a = useState([]), treeViewOptions = _a[0], setTreeViewOptions = _a[1];
    var _b = useState(), folderId = _b[0], setFolderId = _b[1];
    // undefined folderId will load root folders by default
    var _c = useFolderSearch({
        folderIds: folderId && folderId > 0 ? [folderId] : undefined,
        withChildrenList: true
    }), folderTrees = _c.folderTrees, loading = _c.loading;
    useEffect(function () {
        if (loading) {
            setTreeViewOptions(function (oldOptions) {
                var clonedOptions = cloneDeep(oldOptions);
                updateLoadingState(clonedOptions, folderId, true);
                return clonedOptions;
            });
        }
        else if (folderTrees) {
            setTreeViewOptions(function (oldOptions) {
                var _a;
                var clonedOptions = cloneDeep(oldOptions);
                updateLoadingState(clonedOptions, undefined, false);
                return addTreeOption(folderId, clonedOptions, folderId && folderId > 0
                    ? folderTreeToFolderOption((_a = folderTrees[0]) === null || _a === void 0 ? void 0 : _a.children)
                    : kpkFolderTreeToFolderOption(folderTrees));
            });
        }
    }, [folderId, folderTrees, loading]);
    return {
        treeViewOptions: treeViewOptions,
        setFolderId: setFolderId
    };
};
/**
 * Get folders for autocomplete view
 */
var useFolderFieldAutocomplete = function () {
    var _a = useState([]), autocompleteOptions = _a[0], setAutocompleteOptions = _a[1];
    var _b = useState(), query = _b[0], setQuery = _b[1];
    // Setting an empty folderIds array to avoid loading root folders
    var _c = useFolderSearch({
        folderIds: [],
        query: query,
        withChildrenList: false
    }), folderTrees = _c.folderTrees, loading = _c.loading;
    useEffect(function () {
        if (!loading && folderTrees) {
            setAutocompleteOptions(kpkFolderTreeToFolderOption(folderTrees));
        }
    }, [folderTrees, loading]);
    return {
        autocompleteOptions: autocompleteOptions,
        setQuery: setQuery
    };
};
/**
 * Get folder field initial values
 */
var useFolderFieldInitialOptions = function (_a) {
    var folderIds = _a.folderIds;
    var _b = useState([]), initialOptions = _b[0], setInitialOptions = _b[1];
    var _c = useFolderSearch({
        folderIds: folderIds,
        withChildrenList: false
    }), folderTrees = _c.folderTrees, loading = _c.loading;
    useEffect(function () {
        if (!loading) {
            if (folderTrees && folderIds && folderIds.length > 0) {
                setInitialOptions(kpkFolderTreeToFolderOption(folderTrees));
            }
            else {
                setInitialOptions([]);
            }
        }
    }, [folderIds, folderIds.length, folderTrees, loading]);
    return {
        initialOptions: initialOptions
    };
};
var kpkFolderTreeToFolderOption = function (kpkFlderTree) {
    if (!kpkFlderTree || kpkFlderTree.length === 0) {
        return [];
    }
    return kpkFlderTree.map(function (folder) {
        return {
            id: folder.folder.id,
            label: folder.folder.title || "".concat(folder.folder.id),
            children: folder.folder.hasChildren ? undefined : [],
            path: folder.folder.path
        };
    });
};
var folderTreeToFolderOption = function (folderTree) {
    if (!folderTree || folderTree.length === 0) {
        return [];
    }
    return folderTree.map(function (folder) {
        return {
            id: folder.id,
            label: folder.title || "".concat(folder.id),
            children: folder.hasChildren ? undefined : [],
            path: folder.path
        };
    });
};

export { useFolderField };
//# sourceMappingURL=hooks.js.map
